import { getToken } from '../utils/auth';

import { API_BASE_URL } from './URL_Header';

export const fetchTypes = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/GetAllTypes`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch types');
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchTypeById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/GetType/${id}`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch type');
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchTypeNameById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/GetTypeName/${id}`);
    if (!response.ok) throw new Error('Failed to fetch type name');
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const createType = async (type) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/AddType`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify({
        ...type,
        Sales: 0,
        IsAvailable: true // Default to available
      })
    });
    if (!response.ok) throw new Error('Failed to create type');
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const updateType = async (id, type) => {
  try {
    // Ensure the ID is included in the type object
    const typeWithId = {
      ...type,
      typeId: id
    };
    
    console.log("Sending update request with payload:", typeWithId); // Debug log
    
    const response = await fetch(`${API_BASE_URL}/Type/EditType/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify(typeWithId)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Update failed with status:", response.status, "Response:", errorText);
      throw new Error(`Failed to update type: ${response.status}`);
    }
    
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    console.error("UpdateType API error:", err);
    throw new Error(err.message);
  }
};

export const deleteType = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/DeleteType/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getToken()}`
      }
    });
    if (!response.ok) throw new Error('Failed to delete type');
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchTypeIdByName = async (name) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/GetTypeIdByName/${encodeURIComponent(name)}`);
    if (!response.ok) throw new Error('Failed to fetch type ID');
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const bulkSetTypeAvailability = async (typeIds, isAvailable) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/BulkSetAvailability`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        typeIds,
        isAvailable,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to update types availability');
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const bulkSetTypeAvailabilityByNames = async (names, isAvailable) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Type/BulkSetAvailability`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        names,
        isAvailable,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to update types availability');
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const updateTypeStock = async (id, stockQuantity, lowStockThreshold, unavailableThreshold) => {
  const body = { stockQuantity };
  if (lowStockThreshold !== undefined && lowStockThreshold !== null && lowStockThreshold !== "") {
    body.lowStockThreshold = Number(lowStockThreshold);
  }
  if (unavailableThreshold !== undefined && unavailableThreshold !== null && unavailableThreshold !== "") {
    body.unavailableThreshold = Number(unavailableThreshold);
  }

  const response = await fetch(`${API_BASE_URL}/Type/UpdateStock/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to update type stock");
  }

  return await response.json();
};

export const fetchCaseStyleSales = async (brandId) => {
  const response = await fetch(`${API_BASE_URL}/Type/GetCaseStyleSales?brandId=${brandId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) throw new Error("Failed to fetch case style sales");
  return await response.json();
};

export const fetchCaseStyleStocks = async (brandId) => {
  const response = await fetch(`${API_BASE_URL}/Type/GetCaseStyleStocks?brandId=${brandId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) throw new Error("Failed to fetch case style stocks");
  return await response.json();
};

export const addTypeStock = async (id, quantity, caseStyleId) => {
  const body = { quantity: Number(quantity) };
  if (caseStyleId) body.caseStyleId = Number(caseStyleId);
  const response = await fetch(`${API_BASE_URL}/Type/AddStock/${id}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to add type stock");
  }

  return await response.json();
};

export const fetchAssignedCaseStyleIds = async (typeId) => {
  const response = await fetch(`${API_BASE_URL}/Type/GetAssignedCaseStyleIds/${typeId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) throw new Error("Failed to fetch assigned case styles");
  return await response.json();
};

export const setTypeCaseStyles = async (typeId, caseStyleIds) => {
  const response = await fetch(`${API_BASE_URL}/Type/SetCaseStyles/${typeId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ caseStyleIds }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update case styles for type");
  }
  return response.status === 204 ? {} : await response.json();
};

export const setTypeChildTypes = async (id, childTypeIds) => {
  const response = await fetch(`${API_BASE_URL}/Type/SetChildTypes/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ childTypeIds }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update child types");
  }
  return response.status === 204 ? {} : await response.json();
};

export const searchTypes = async (q) => {
  const response = await fetch(`${API_BASE_URL}/Type/Search?q=${encodeURIComponent(q || "")}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) throw new Error("Failed to search types");
  return await response.json();
};

export const fetchCaseStyleInventory = async (typeId) => {
  const response = await fetch(`${API_BASE_URL}/Type/GetCaseStyleInventory/${typeId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) throw new Error("Failed to fetch case style inventory");
  return await response.json();
};

export const updateCaseStyleInventory = async (typeId, payload) => {
  const response = await fetch(`${API_BASE_URL}/Type/UpdateCaseStyleInventory/${typeId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update case style inventory");
  }
  return await response.json();
};

export const fetchCaseStylesForType = async (typeId) => {
  if (!typeId) return [];
  const response = await fetch(`${API_BASE_URL}/Type/GetCaseStyles/${typeId}`);
  if (!response.ok) throw new Error("Failed to fetch case styles");
  return await response.json();
};